import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Breadcrumbs } from '../../components';
import {
  Button,
  CondensedDangerZoneAction,
  IconButton,
  ItemList,
  ItemListItem,
  Paper,
  Pill,
  PillSelect,
  PillSelectOption,
} from '../../components/design-system';
import {
  ADD_FIND_GROUP_COLLABORATOR_MUTATION,
  GET_MY_CONTACT_QUERY,
  GET_MY_CONTACTS_QUERY,
  REMOVE_FIND_GROUP_COLLABORATOR_MUTATION,
  REMOVE_CONTACT_MUTATION,
  REMOVE_SITE_COLLABORATOR_MUTATION,
  UPDATE_SITE_COLLABORATOR_LEVEL_MUTATION,
} from '../../gql';
import { formatDate } from '../../helpers';
import {
  ContactDetailData,
  ContactSharedItem,
  getContactInitials,
  getContactName,
} from './types';

interface SharingPanelProps {
  contactName: string;
  direction: 'byMe' | 'withMe';
  items: ContactSharedItem[];
  onLevelChange: (item: ContactSharedItem, level: number) => void;
  onRemove: (item: ContactSharedItem) => void;
  updating: boolean;
}

interface SharedItemActionsProps {
  editable: boolean;
  item: ContactSharedItem;
  onLevelChange: (item: ContactSharedItem, level: number) => void;
  onRemove: (item: ContactSharedItem) => void;
  updating: boolean;
}

const headerActionsStyles = {
  alignItems: { xs: 'flex-start', sm: 'center' },
  justifyContent: 'space-between',
};
const profileStyles = { p: { xs: 2, sm: 2.5 } };
const avatarStyles = {
  width: 68,
  height: 68,
  fontSize: '1.5rem',
  bgcolor: 'action.selected',
  color: 'text.primary',
};
const profileTextStyles = { minWidth: 0 };
const profileStatusStyles = { ml: { sm: 'auto' } };
const panelGridStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
  gap: 2,
};
const panelStyles = { minWidth: 0 };
const countPillStyles = { gap: 0.5 };
const sectionHeadingStyles = { color: 'text.secondary', fontWeight: 600 };
const emptyStyles = { color: 'text.secondary', py: 1 };
const dangerStyles = { borderColor: 'error.dark' };
const itemActionsStyles = {
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
};
const accessSelectStyles = {
  minWidth: 132,
  '& .MuiSelect-icon': { opacity: 1 },
};
const accessLabelStyles = { alignItems: 'center', display: 'flex', gap: 0.5 };
const staticAccessStyles = { gap: 0.5 };

const getLevelIcon = (level: number) => {
  if (level === 0) {
    return <VisibilityOutlinedIcon fontSize="small" />;
  }

  if (level === 1) {
    return <AddCircleOutlineIcon fontSize="small" />;
  }

  return <EditOutlinedIcon fontSize="small" />;
};

const SharedItemActions = React.memo(
  ({
    editable,
    item,
    onLevelChange,
    onRemove,
    updating,
  }: SharedItemActionsProps) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const levelLabel = useCallback(
      (level: number) => {
        const labels = [
          intl.formatMessage({
            id: 'access.level.read',
            defaultMessage: 'Read',
          }),
          intl.formatMessage({
            id: 'access.level.add',
            defaultMessage: 'Add',
          }),
          intl.formatMessage({
            id: 'access.level.edit',
            defaultMessage: 'Edit',
          }),
        ];

        return labels[level] || String(level);
      },
      [intl]
    );
    const levelOptions = useMemo<PillSelectOption[]>(
      () => [
        {
          value: 0,
          label: (
            <Box component="span" sx={accessLabelStyles}>
              {getLevelIcon(0)} {levelLabel(0)}
            </Box>
          ),
          tone: 'neutral',
        },
        {
          value: 1,
          label: (
            <Box component="span" sx={accessLabelStyles}>
              {getLevelIcon(1)} {levelLabel(1)}
            </Box>
          ),
          tone: 'info',
        },
        {
          value: 2,
          label: (
            <Box component="span" sx={accessLabelStyles}>
              {getLevelIcon(2)} {levelLabel(2)}
            </Box>
          ),
          tone: 'success',
        },
      ],
      [levelLabel]
    );
    const handleOpen = useCallback(() => {
      navigate(
        item.kind === 'SITE' ? `/s/${item.id}` : `/finds/groups/${item.id}`
      );
    }, [item.id, item.kind, navigate]);
    const handleLevelChange = useCallback(
      (event: SelectChangeEvent<number | string>) => {
        onLevelChange(item, Number(event.target.value));
      },
      [item, onLevelChange]
    );
    const handleRemove = useCallback(() => {
      onRemove(item);
    }, [item, onRemove]);

    return (
      <Stack direction="row" gap={0.5} sx={itemActionsStyles}>
        <Button
          density="compact"
          endIcon={<ArrowForwardIcon />}
          onClick={handleOpen}
          tone="neutral"
          variant="outlined"
        >
          {intl.formatMessage({
            id: 'general.open',
            defaultMessage: 'Open',
          })}
        </Button>
        {editable ? (
          <>
            <PillSelect
              disabled={updating}
              inputProps={{ 'aria-label': levelLabel(item.level) }}
              onChange={handleLevelChange}
              options={levelOptions}
              sx={accessSelectStyles}
              value={item.level}
            />
            <IconButton
              aria-label={intl.formatMessage({
                id: 'contacts.detail.removeSharing',
                defaultMessage: 'Remove sharing',
              })}
              density="compact"
              disabled={updating}
              onClick={handleRemove}
              tone="danger"
              variant="ghost"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </>
        ) : (
          <Pill
            tone={
              item.level > 1 ? 'success' : item.level > 0 ? 'info' : 'neutral'
            }
            sx={staticAccessStyles}
          >
            {getLevelIcon(item.level)} {levelLabel(item.level)}
          </Pill>
        )}
      </Stack>
    );
  }
);

SharedItemActions.displayName = 'SharedItemActions';

const buildListItems = (
  items: ContactSharedItem[],
  editable: boolean,
  onLevelChange: (item: ContactSharedItem, level: number) => void,
  onRemove: (item: ContactSharedItem) => void,
  updating: boolean
): ItemListItem[] =>
  items.map((item) => ({
    action: (
      <SharedItemActions
        editable={editable}
        item={item}
        onLevelChange={onLevelChange}
        onRemove={onRemove}
        updating={updating}
      />
    ),
    icon:
      item.kind === 'SITE' ? (
        <LocationOnOutlinedIcon />
      ) : (
        <GroupsOutlinedIcon />
      ),
    id: `${item.kind}-${item.id}`,
    label: item.name,
  }));

const SharingSection = React.memo(
  ({
    emptyLabel,
    editable,
    items,
    label,
    onLevelChange,
    onRemove,
    updating,
  }: {
    emptyLabel: string;
    editable: boolean;
    items: ContactSharedItem[];
    label: string;
    onLevelChange: (item: ContactSharedItem, level: number) => void;
    onRemove: (item: ContactSharedItem) => void;
    updating: boolean;
  }) => {
    const listItems = useMemo(
      () => buildListItems(items, editable, onLevelChange, onRemove, updating),
      [editable, items, onLevelChange, onRemove, updating]
    );

    return (
      <Stack gap={0.75}>
        <Typography variant="body2" sx={sectionHeadingStyles}>
          {label}
        </Typography>
        {items.length ? (
          <ItemList items={listItems} showCount={3} />
        ) : (
          <Typography variant="body2" sx={emptyStyles}>
            {emptyLabel}
          </Typography>
        )}
      </Stack>
    );
  }
);

SharingSection.displayName = 'SharingSection';

const SharingPanel = React.memo(
  ({
    contactName,
    direction,
    items,
    onLevelChange,
    onRemove,
    updating,
  }: SharingPanelProps) => {
    const intl = useIntl();
    const sites = useMemo(() => items.filter((item) => item.kind === 'SITE'), [
      items,
    ]);
    const groups = useMemo(
      () => items.filter((item) => item.kind === 'FIND_GROUP'),
      [items]
    );
    const title =
      direction === 'withMe'
        ? intl.formatMessage(
            {
              id: 'contacts.detail.sharesWithYou',
              defaultMessage: '{name} shares with you',
            },
            { name: contactName }
          )
        : intl.formatMessage(
            {
              id: 'contacts.detail.youShareWith',
              defaultMessage: 'You share with {name}',
            },
            { name: contactName }
          );

    return (
      <Paper sx={panelStyles}>
        <Stack gap={2}>
          <Typography component="h3" variant="h6">
            {title}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Pill sx={countPillStyles}>
              <LocationOnOutlinedIcon fontSize="small" />
              {intl.formatMessage(
                {
                  id: 'contacts.sharing.siteCount',
                  defaultMessage:
                    '{count, plural, one {# site} other {# sites}}',
                },
                { count: sites.length }
              )}
            </Pill>
            <Pill sx={countPillStyles}>
              <GroupsOutlinedIcon fontSize="small" />
              {intl.formatMessage(
                {
                  id: 'contacts.sharing.groupCount',
                  defaultMessage:
                    '{count, plural, one {# group} other {# groups}}',
                },
                { count: groups.length }
              )}
            </Pill>
          </Stack>
          <SharingSection
            emptyLabel={intl.formatMessage({
              id: 'contacts.detail.sites.empty',
              defaultMessage: 'No shared sites',
            })}
            editable={direction === 'byMe'}
            items={sites}
            label={intl.formatMessage({
              id: 'map.tab.sites',
              defaultMessage: 'Sites',
            })}
            onLevelChange={onLevelChange}
            onRemove={onRemove}
            updating={updating}
          />
          <SharingSection
            emptyLabel={intl.formatMessage({
              id: 'contacts.detail.groups.empty',
              defaultMessage: 'No shared groups',
            })}
            editable={direction === 'byMe'}
            items={groups}
            label={intl.formatMessage({
              id: 'finds.menu.groups',
              defaultMessage: 'Groups',
            })}
            onLevelChange={onLevelChange}
            onRemove={onRemove}
            updating={updating}
          />
        </Stack>
      </Paper>
    );
  }
);

SharingPanel.displayName = 'SharingPanel';

const ContactDetail = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { contactId } = useParams();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { data, loading } = useQuery(GET_MY_CONTACT_QUERY, {
    variables: { id: contactId },
    fetchPolicy: 'cache-first',
    skip: !contactId,
  });
  const [removeContact, { loading: removing }] = useMutation(
    REMOVE_CONTACT_MUTATION,
    {
      refetchQueries: [{ query: GET_MY_CONTACTS_QUERY }],
    }
  );
  const detailRefetchQueries = useMemo(
    () =>
      contactId
        ? [
            { query: GET_MY_CONTACT_QUERY, variables: { id: contactId } },
            { query: GET_MY_CONTACTS_QUERY },
          ]
        : [],
    [contactId]
  );
  const [updateSiteLevel, { loading: updatingSite }] = useMutation(
    UPDATE_SITE_COLLABORATOR_LEVEL_MUTATION,
    {
      refetchQueries: detailRefetchQueries,
    }
  );
  const [updateFindGroupLevel, { loading: updatingFindGroup }] = useMutation(
    ADD_FIND_GROUP_COLLABORATOR_MUTATION,
    {
      refetchQueries: detailRefetchQueries,
    }
  );
  const [removeSiteSharing, { loading: removingSiteSharing }] = useMutation(
    REMOVE_SITE_COLLABORATOR_MUTATION,
    {
      refetchQueries: detailRefetchQueries,
    }
  );
  const [
    removeFindGroupSharing,
    { loading: removingFindGroupSharing },
  ] = useMutation(REMOVE_FIND_GROUP_COLLABORATOR_MUTATION, {
    refetchQueries: detailRefetchQueries,
  });
  const contact = data?.getMyContact as ContactDetailData | null | undefined;
  const updatingSharing =
    updatingSite ||
    updatingFindGroup ||
    removingSiteSharing ||
    removingFindGroupSharing;
  const handleBack = useCallback(() => {
    navigate('/contacts');
  }, [navigate]);
  const handleConfirmOpen = useCallback(() => {
    setConfirmOpen(true);
  }, []);
  const handleConfirmClose = useCallback(() => {
    if (!removing) {
      setConfirmOpen(false);
    }
  }, [removing]);
  const handleRemove = useCallback(async () => {
    if (!contactId) {
      return;
    }

    await removeContact({ variables: { id: contactId } });
    navigate('/contacts');
  }, [contactId, navigate, removeContact]);
  const handleLevelChange = useCallback(
    async (item: ContactSharedItem, level: number) => {
      if (!contact?.responderUser) {
        return;
      }

      if (item.kind === 'SITE') {
        await updateSiteLevel({
          variables: {
            level,
            siteId: item.id,
            userId: contact.responderUser,
          },
        });

        return;
      }

      await updateFindGroupLevel({
        variables: {
          groupId: item.id,
          level,
          userId: contact.responderUser,
        },
      });
    },
    [contact?.responderUser, updateFindGroupLevel, updateSiteLevel]
  );
  const handleRemoveSharing = useCallback(
    async (item: ContactSharedItem) => {
      if (!contact?.responderUser) {
        return;
      }

      if (item.kind === 'SITE') {
        await removeSiteSharing({
          variables: {
            siteId: item.id,
            userId: contact.responderUser,
          },
        });

        return;
      }

      await removeFindGroupSharing({
        variables: {
          groupId: item.id,
          userId: contact.responderUser,
        },
      });
    },
    [contact?.responderUser, removeFindGroupSharing, removeSiteSharing]
  );

  if (loading) {
    return (
      <Stack gap={2}>
        <Skeleton width={260} />
        <Skeleton height={130} variant="rounded" />
        <Skeleton height={360} variant="rounded" />
      </Stack>
    );
  }

  if (!contact) {
    return (
      <Stack gap={2}>
        <Breadcrumbs />
        <Paper>
          <Typography>
            {intl.formatMessage({
              id: 'contacts.detail.notFound',
              defaultMessage: 'Contact not found',
            })}
          </Typography>
        </Paper>
      </Stack>
    );
  }

  const contactName = getContactName(contact);

  return (
    <Stack gap={2.5} pb={6}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        sx={headerActionsStyles}
      >
        <Breadcrumbs />
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          tone="neutral"
        >
          {intl.formatMessage({
            id: 'contacts.detail.back',
            defaultMessage: 'Back to contacts',
          })}
        </Button>
      </Stack>

      <Paper sx={profileStyles}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={2}
        >
          <Avatar sx={avatarStyles}>{getContactInitials(contact)}</Avatar>
          <Stack sx={profileTextStyles}>
            <Typography component="h1" variant="h5" fontWeight={700} noWrap>
              {contactName}
            </Typography>
            <Typography color="text.secondary" variant="body2" noWrap>
              {contact.email}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {intl.formatMessage(
                {
                  id: 'contacts.detail.addedOn',
                  defaultMessage: 'Contact since {date}',
                },
                { date: formatDate(contact.createdAt) }
              )}
            </Typography>
          </Stack>
          <Pill tone="success" sx={profileStatusStyles}>
            <CheckIcon fontSize="small" />
            {intl.formatMessage({
              id: 'contacts.detail.approved',
              defaultMessage: 'Approved contact',
            })}
          </Pill>
        </Stack>
      </Paper>

      <Typography component="h2" variant="h6" fontWeight={700}>
        {intl.formatMessage({
          id: 'contacts.detail.sharedContent',
          defaultMessage: 'Shared content',
        })}
      </Typography>
      <Box sx={panelGridStyles}>
        <SharingPanel
          contactName={contactName}
          direction="withMe"
          items={contact.sharedWithMe}
          onLevelChange={handleLevelChange}
          onRemove={handleRemoveSharing}
          updating={updatingSharing}
        />
        <SharingPanel
          contactName={contactName}
          direction="byMe"
          items={contact.sharedByMe}
          onLevelChange={handleLevelChange}
          onRemove={handleRemoveSharing}
          updating={updatingSharing}
        />
      </Box>

      <Paper sx={dangerStyles}>
        <CondensedDangerZoneAction
          actionIcon={<DeleteOutlineIcon />}
          actionLabel={intl.formatMessage({
            id: 'contacts.detail.remove',
            defaultMessage: 'Remove contact',
          })}
          disabled={removing}
          label={intl.formatMessage({
            id: 'contacts.detail.dangerHelp',
            defaultMessage: 'Removing this contact requires confirmation.',
          })}
          onAction={handleConfirmOpen}
        />
      </Paper>

      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>
          {intl.formatMessage({
            id: 'contacts.detail.removeConfirmTitle',
            defaultMessage: 'Remove contact?',
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage(
              {
                id: 'contacts.detail.removeConfirmDescription',
                defaultMessage:
                  'Remove {name} from your contacts? Existing sharing permissions are unchanged.',
              },
              { name: contactName }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} tone="neutral">
            {intl.formatMessage({
              id: 'general.cancel',
              defaultMessage: 'Cancel',
            })}
          </Button>
          <Button disabled={removing} onClick={handleRemove} tone="danger">
            {intl.formatMessage({
              id: 'contacts.detail.remove',
              defaultMessage: 'Remove contact',
            })}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export { ContactDetail };
